import React, { useEffect, useRef } from 'react';
import BpmAdjustControl from './BpmAdjustControl';
import { features } from '../../../config/features';

interface SaveActionsDropdownProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  onGeneratePdfOnly: () => void;
  onGenerateAudioOnly: () => void;
  isGenerating: boolean;
  isGeneratingAudio: boolean;
  isMobile: boolean;
  currentBpm: number;
  detectedBpm: number | undefined;
  onBpmChange: (bpm: number) => void;
  bpmDirty: boolean;
}

const SaveActionsDropdown: React.FC<SaveActionsDropdownProps> = ({
  anchorRef,
  onClose,
  onGeneratePdfOnly,
  onGenerateAudioOnly,
  isGenerating,
  isGeneratingAudio,
  isMobile,
  currentBpm,
  detectedBpm,
  onBpmChange,
  bpmDirty,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const pointerDownInsideRef = useRef<boolean>(false);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      const menu = menuRef.current;
      const anchor = anchorRef.current;
      if (!menu) return;
      const target = e.target as Node;
      if (menu.contains(target)) {
        pointerDownInsideRef.current = true;
        return;
      }
      if (anchor && anchor.contains(target)) {
        pointerDownInsideRef.current = false;
        return;
      }
      pointerDownInsideRef.current = false;
      onClose();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [anchorRef, onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        anchorRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [anchorRef, onClose]);

  const handlePdfClick = () => {
    if (isGenerating) return;
    onClose();
    onGeneratePdfOnly();
  };

  const handleAudioClick = () => {
    if (isGeneratingAudio) return;
    onClose();
    onGenerateAudioOnly();
  };

  const handleMenuItemKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const menu = menuRef.current;
      if (!menu) return;
      const items = menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
      const current = e.currentTarget;
      const idx = Array.from(items).indexOf(current);
      if (idx < 0) return;
      let nextIdx: number;
      if (e.key === 'ArrowDown') {
        nextIdx = (idx + 1) % items.length;
      } else {
        nextIdx = (idx - 1 + items.length) % items.length;
      }
      items[nextIdx].focus();
    }
  };

  const widthClass = isMobile ? 'w-[calc(100vw-1rem)] max-w-xs' : 'w-64';

  return (
    <div
      ref={menuRef}
      role="menu"
      id="save-actions-menu"
      aria-labelledby="save-split-button-trigger"
      className={`absolute right-0 top-full mt-2 z-50 ${widthClass} bg-slate-800 border border-slate-600 rounded-xl shadow-xl max-h-[min(70vh,480px)] overflow-y-auto`}
    >
      <button
        type="button"
        role="menuitem"
        tabIndex={-1}
        onClick={handlePdfClick}
        onKeyDown={handleMenuItemKeyDown}
        aria-disabled={isGenerating}
        aria-label="PDFのみ生成"
        className={`block w-full px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-2 ${
          isGenerating ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        <span aria-hidden="true">📄</span>
        <span>PDFのみ生成</span>
      </button>

      {features.enableAudio && (
        <>
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={handleAudioClick}
            onKeyDown={handleMenuItemKeyDown}
            aria-disabled={isGeneratingAudio}
            aria-label="音源のみ生成"
            className={`block w-full px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-2 ${
              isGeneratingAudio ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            <span aria-hidden="true">♪</span>
            <span>{isGeneratingAudio ? '生成中...' : '音源のみ生成'}</span>
            {bpmDirty && (
              <span
                data-testid="audio-only-dirty-dot"
                className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-400"
              />
            )}
          </button>

          <div className="border-t border-slate-600 my-1" />

          <BpmAdjustControl
            currentBpm={currentBpm}
            detectedBpm={detectedBpm}
            onChange={onBpmChange}
          />
        </>
      )}

    </div>
  );
};

export default SaveActionsDropdown;
